import React, { useEffect, useState, useMemo, useRef } from 'react';

import { DateField } from 'src/components/base/fields/DateField';
import { TimeField } from 'src/components/base/fields/TimeField';
import { DateTimeField } from 'src/components/base/fields/DateTimeField';
import { getDefaultValue as getDefaultDT } from 'src/components/base/fields/dateTimeUtils';

import * as CF from 'src/components/base/fields/salesforce/CheckboxField';
import * as LF from 'src/components/base/fields/salesforce/LookupField';
import * as SF from 'src/components/base/fields/salesforce/SimpleField';
import * as PF from 'src/components/base/fields/salesforce/PicklistField';
import * as RTF from 'src/components/base/fields/salesforce/RichTextField';

import { defaultFormat } from 'src/components/base/fields/SubstitutionPicker';

import * as QA from 'shared/utils/queryAnalysis'

import { useTraceUpdate } from './../utils';


export function Field(props) {
  const { rawField, substitutionProps } = props;
  if (!rawField) { 
    return null;
  }

  const s = useMemo(() => {
    const {
      allowSubstitutions = true, 
      showPicker = true, 
      validate = validateSubstitutions,
      formatSelection = defaultFormat,
    } = substitutionProps ?? {};
    return { allowSubstitutions, showPicker, validate, formatSelection };
  }, [substitutionProps]);  

  // useTraceUpdate(props, 'Field');

  const Component = useField(rawField);
  return Component ? <Component {...props} substitutionProps={s} /> : null;
}

const Ftn = QA.FieldTypeName;
const components = [
  { types: LF.SupportedTypes, component: LF.LookupField },
  { types: PF.SupportedTypes, component: PF.PicklistField },
  { types: [Ftn.Time], component: TimeField },
  { types: [Ftn.DateTime], component: DateTimeField },
  { types: [Ftn.Date], component: DateField, },
  { types: SF.SupportedTypes, component: SF.SimpleField, },
  { types: [Ftn.Boolean], component: CF.CheckboxField, },
  { types: RTF.SupportedTypes, component: RTF.RichTextField, },
];  


function useField(rawField) {
  return useMemo(() => {
    const { type } = rawField;
    const t = components.find(x => x.types.includes(type));
    return t?.component ?? SF.SimpleField;
  }, [rawField]);
}


export function getDefaultValue(rawField, convert = {}) {
  const { type } = rawField;
  const { picklist, simple, checkbox, lookup, time, rich } = convert;
  
  const typesInfo = [
    { types: SF.SupportedTypes, fn: SF.getDefaultValue, cvt: simple },
    { types: PF.SupportedTypes, fn: PF.getDefaultOption, cvt: picklist },
    { types: LF.SupportedTypes, fn: LF.getDefaultValue, cvt: lookup },
    { types: [Ftn.Date, Ftn.DateTime, Ftn.Time], fn: getDefaultDT, cvt: time },
    { types: [Ftn.Boolean], fn: CF.getDefaultOption, cvt: checkbox, },
    { types: RTF.SupportedTypes, fn: RTF.getDefaultValue, cvt: rich }
  ];

  const { cvt, fn } = typesInfo.find(x => x.types.includes(type)) ?? typesInfo[0];
  const v = fn(rawField);
  return cvt ? cvt(v, rawField) : v;
}


export function validateSubstitutions(substitutions) {
  return substitutions.reduce((out, x) => {
    const { expression, substitutionString } = x;
    const { range, table } = expression;
    if (table) {
      out.push(`${substitutionString} - tables are not allowed`)
    }
    else if (range) {
      const { address, name } = range;
      if (name) {
        out.push(`${substitutionString} - named ranges are not allowed`)
      }
      else if (!address.isSingleCell) {
        out.push(`${substitutionString} should have only one cell`)
      }
    }
    return out; 
  }, []).join(", ");
}


export default Field;